import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBarRef, MAT_SNACK_BAR_DATA } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { interval, Subscription } from 'rxjs';

export interface SnackbarData {
  message: string;
  type: 'success' | 'error';
  action?: string;
  duration?: number;
}

@Component({
  selector: 'app-snackbar',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './snackbar-common.component.html',
  styleUrls: ['./snackbar-common.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('void', style({
        transform: 'translateX(100%)',
        opacity: 0
      })),
      state('enter', style({
        transform: 'translateX(0)',
        opacity: 1
      })),
      state('leave', style({
        transform: 'translateX(100%)',
        opacity: 0
      })),
      transition('void => enter', animate('225ms cubic-bezier(0.0, 0.0, 0.2, 1)')),
      transition('enter => leave', animate('195ms cubic-bezier(0.4, 0.0, 1, 1)'))
    ]),
    trigger('progressAnimation', [
      transition('* => *', [
        style({ width: '0%' }),
        animate('{{ duration }}ms linear', style({ width: '100%' }))
      ])
    ])
  ]
})
export class SnackbarComponent implements OnInit, OnDestroy {
  private snackBarRef = inject(MatSnackBarRef<SnackbarComponent>);
  public data = inject<SnackbarData>(MAT_SNACK_BAR_DATA);
  
  animationState: 'void' | 'enter' | 'leave' = 'enter';
  progressValue: number = 0; // Start at 0 for left-to-right
  private progressSubscription?: Subscription;

  ngOnInit(): void {
    if (this.data.duration) {
      // Update progress bar
      const totalSteps = 100;
      const step = this.data.duration / totalSteps;
      
      this.progressSubscription = interval(step)
        .subscribe(() => {
          this.progressValue += 1; // Increase for left-to-right
          if (this.progressValue >= 100) {
            this.dismiss();
          }
        });
    }
  }

  ngOnDestroy(): void {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  dismiss(): void {
    this.animationState = 'leave';
    setTimeout(() => {
      this.snackBarRef.dismiss();
    }, 100);
  }
}